import React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, provideIntlService, registerForIntl, registerForLocalization } from '@progress/kendo-react-intl';
import { GridSize, Grid, InlineBadge, DateFormatter } from '../../shared/components';
import { loadCompetencyRequestListPage, findCompetencyRequests, selectCompetencyRequestList, selectAllCompetencyRequestList, initMultiSign, toggleConfirmDialog, sendCompetencyCertificates } from '../actions/competency-request-list-page-actions';
import { withRouter } from '../../../components/withRouter';
import { getStudentCompetencyStatusColor } from '../../../utils/studentCompetencyStatusColors';
import styled from 'styled-components';
import { Colors, Text } from '../../../ui';
import { Checkbox } from '@progress/kendo-react-inputs';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { STUDENT_COMPETENCY_STATUS } from '../../../resources/studentCompetencyStatus';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const CellDataContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatusContainer = styled(CellDataContainer)`
  align-items: center;
`;

class CompetencyRequestList extends React.PureComponent {

  constructor() {
    super();
    this.handlePageChange = this.handlePageChange.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
    this.renderResident = this.renderResident.bind(this);
    this.renderCompetency = this.renderCompetency.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.getRowLink = this.getRowLink.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleSignClick = this.handleSignClick.bind(this);
    this.handleSendClick = this.handleSendClick.bind(this);
    this.renderSelected = this.renderSelected.bind(this);
  }

  async componentDidMount() {
    const searchParams = new URLSearchParams(this.props.location.search);
    await this.props.loadCompetencyRequestListPage({ searchParams });
  }

  componentWillUnmount() {
    if (this.props.selectedAll) {
      this.props.selectAllCompetencyRequestList();
    }
  }

  render() {
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <Grid
          loading={this.props.loading}
          data={this.props.competencyRequests}
          skip={this.props.skip}
          take={this.props.take}
          total={this.props.total}
          sort={this.props.sorting}
          onPageChange={this.handlePageChange}
          onRowClick={this.handleRowClick}
          rowLink={this.getRowLink}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          dataItemKey='id'
          selectedField='isSelected'
          onSelectionChange={this.handleSelect}
          columns={[
            {
              field: 'isSelected',
              width: '50px',
              skipNavLink: true,
              cell: this.renderSelected,
              headerCell: () => (
                <span>
                  <Checkbox checked={this.props.selectedAll} onClick={this.handleSelectAll} />
                </span>
              )
            }, {
              field: 'competency',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('competencyRequest.competency'),
              sortable: false,
              cell: this.renderCompetency
            }, {
              field: 'resident',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('competencyRequest.resident'),
              sortable: false,
              cell: this.renderResident
            }, {
              field: 'status',
              className: 'align-center',
              headerClassName: 'align-center',
              title: localizationService.toLanguageString('competencyRequest.status'),
              sortable: false,
              minGridWidth: GridSize.small,
              cell: this.renderStatus
            }
          ]}
        >
          <GridToolbar>
            <Button
              themeColor='primary'
              disabled={!this.props.selectedCompetencies?.length > 0}
              onClick={() => this.props.toggleConfirmDialog({
                localizationService,
                confirmDialogActionText: localizationService.toLanguageString('competencyRequest.signAction'),
                confirmDialogFunction: this.handleSignClick,
                statusesForActionValidation: [ STUDENT_COMPETENCY_STATUS.TO_BE_APPROVED, STUDENT_COMPETENCY_STATUS.TO_BE_SIGNED ],
                userIdForActionValidation: this.props.userId
              })}
            >
              {localizationService.toLanguageString('competencyRequest.sign')}
            </Button>
            {this.props.userIsAdministrator &&
              <Button
                themeColor='primary'
                disabled={!this.props.selectedCompetencies?.length > 0}
                onClick={() => this.props.toggleConfirmDialog({
                  localizationService,
                  confirmDialogActionText: localizationService.toLanguageString('competencyRequest.sendAction'),
                  confirmDialogFunction: this.handleSendClick,
                  statusesForActionValidation: [ STUDENT_COMPETENCY_STATUS.ACQUIRED, STUDENT_COMPETENCY_STATUS.SENDING_ERROR ]
                })}
              >
                {localizationService.toLanguageString('competencyRequest.send')}
              </Button>
            }
          </GridToolbar>
        </Grid>
        {this.props.isConfirmDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.props.toggleConfirmDialog}>
            <Text>
              {this.props.confirmDialogText}
            </Text>
            <DialogActionsBar>
              {this.props.isConfirmButtonDisabled
                ? <Button themeColor={'primary'} onClick={this.props.toggleConfirmDialog}>{localizationService.toLanguageString('buttons.ok')}</Button>
                :
                <>
                  <Button
                    themeColor={'primary'}
                    onClick={this.props.confirmDialogFunction}
                    disabled={this.props.loading}
                  >
                    {localizationService.toLanguageString('buttons.yes')}
                  </Button>
                  <Button onClick={this.props.toggleConfirmDialog}>{localizationService.toLanguageString('buttons.no')}</Button>
                </>
              }
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  getRowLink(dataItem) {
    return dataItem.id ? `/competency_requests/${dataItem.id}` : null;
  }

  handleRowClick(e) {
    this.props.navigate(this.getRowLink(e.dataItem));
  }

  renderCompetency(e) {
    const { dataItem, className } = e;
    return (
      <td className={className}>
        <CellDataContainer>
          {`${dataItem.competencyAbbreviation} ${dataItem.competency}`}
          <Text textColor={Colors.GRAY_50} variant='caption'>{dataItem.studyProgram}</Text>
        </CellDataContainer>
      </td>
    );
  }

  renderResident(e) {
    const { dataItem, className } = e;
    return (
      <td className={className}>
        <CellDataContainer>
          {dataItem.resident}
          <Text textColor={Colors.GRAY_50} variant='caption'>{dataItem.residentNumber}</Text>
        </CellDataContainer>
      </td>
    );
  }

  renderStatus(e) {
    const { className, dataItem } = e;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    return (
      <td className={className}>
        <StatusContainer>
          {dataItem.status &&
            <InlineBadge themeColor={getStudentCompetencyStatusColor(dataItem.status)}>
              {localizationService.toLanguageString(`studentCompetencyStatus.${dataItem.status}`)}
            </InlineBadge>
          }
          {dataItem.requestDate &&
            <Text textColor={Colors.GRAY_50} variant='caption' noWrap={true}>{DateFormatter(new Date(dataItem.requestDate), intlService)}</Text>
          }
        </StatusContainer>
      </td>
    );
  }

  renderSelected(e) {
    const statusesForValidation = [ STUDENT_COMPETENCY_STATUS.ACQUIRED, STUDENT_COMPETENCY_STATUS.SENDING_ERROR ];
    if (e.dataItem.approvingUserId === this.props.userId) {
      statusesForValidation.push(STUDENT_COMPETENCY_STATUS.TO_BE_APPROVED, STUDENT_COMPETENCY_STATUS.TO_BE_SIGNED);
    }
    const isStatusInvalid = !statusesForValidation.includes(e.dataItem.status);
    const isUserUnauthorized = e.dataItem.approvingUserId !== this.props.userId && !this.props.userIsAdministrator;
    return (
      <td role='gridcell' onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={e.dataItem.isSelected}
          onChange={() => e.selectionChange(e)}
          disabled={isStatusInvalid || isUserUnauthorized}
        />
      </td>
    );
  }

  async handlePageChange(e) {
    await this.props.findCompetencyRequests({ skip: e.page.skip, take: e.page.take });
  }

  handleSelect(e) {
    if (e.dataItem) {
      this.props.selectCompetencyRequestList(e.dataItem);
    }
  }

  handleSelectAll() {
    this.props.selectAllCompetencyRequestList();
  }

  async handleSignClick() {
    await this.props.initMultiSign({ userId: this.props.userId });
    this.props.toggleConfirmDialog();
    if (this.props.signingRedirectUrl) {
      window.open(this.props.signingRedirectUrl, '_self');
    }
  }

  handleSendClick() {
    this.props.sendCompetencyCertificates();
    this.props.toggleConfirmDialog();
  }
}

const mapStateToProps = state => ({
  userId: state.app.userId,
  userIsAdministrator: state.app.userIsAdministrator,
  loading: state.competencyRequestListPage.loading,
  competencyRequests: state.competencyRequestListPage.competencyRequests,
  skip: state.competencyRequestListPage.skip,
  take: state.competencyRequestListPage.take,
  total: state.competencyRequestListPage.total,
  selectedAll: state.competencyRequestListPage.selectedAll,
  selectedCompetencies: state.competencyRequestListPage.selectedCompetencies,
  signingRedirectUrl: state.competencyRequestListPage.signingRedirectUrl,
  isConfirmDialogVisible: state.competencyRequestListPage.isConfirmDialogVisible,
  isConfirmButtonDisabled: state.competencyRequestListPage.isConfirmButtonDisabled,
  confirmDialogText: state.competencyRequestListPage.confirmDialogText,
  confirmDialogFunction: state.competencyRequestListPage.confirmDialogFunction
});

const mapDispatchToProps = dispatch => ({
  loadCompetencyRequestListPage: (payload) => dispatch(loadCompetencyRequestListPage(payload)),
  findCompetencyRequests: (payload) => dispatch(findCompetencyRequests(payload)),
  selectCompetencyRequestList: (payload) => dispatch(selectCompetencyRequestList(payload)),
  selectAllCompetencyRequestList: () => dispatch(selectAllCompetencyRequestList()),
  initMultiSign: (payload) => dispatch(initMultiSign(payload)),
  toggleConfirmDialog: (payload) => dispatch(toggleConfirmDialog(payload)),
  sendCompetencyCertificates: () => dispatch(sendCompetencyCertificates())
});

registerForLocalization(CompetencyRequestList);
registerForIntl(CompetencyRequestList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CompetencyRequestList));